/**
 * Subir un fichero · §S.3
 *
 * ## Tres pasos, y el tercero no es opcional
 *
 * 1. `POST /uploads` — el API valida uso, tipo y tamaño **antes** de firmar nada, y
 *    devuelve una URL firmada.
 * 2. `PUT` **directo al almacén**. El API no recibe bytes: un proceso Bun moviendo
 *    ficheros de 8 MB es la forma más rápida de tumbarlo.
 * 3. `POST /uploads/{id}/complete` — le pregunta al almacén qué hay de verdad.
 *
 * Saltarse el tercero deja **una fila apuntando a un fichero que quizá no existe**, y
 * `GET /files/{id}` responde `404` aunque la fila esté: es la foto rota en la ficha de
 * un alumno. El contrato lo llama «el agujero conocido» y `/complete` existe para
 * cerrarlo.
 *
 * Y hace algo más: **la firma de S3 no incluye el tipo** —firma el host y poco más—,
 * así que con esa URL se puede subir cualquier cosa. Lo que lo impide es la
 * confirmación, que compara lo subido con lo declarado y responde `422 asset_mismatch`.
 * Por eso el `PUT` tiene que mandar el `Content-Type` correcto.
 *
 * ## El ancho y el alto los mide el navegador
 *
 * Las imágenes los exigen, y con razón: sin ellos no se puede reservar el hueco en la
 * pantalla y la página salta cuando carga la foto. Se leen del propio fichero antes de
 * pedir nada, que es el único momento en que se saben gratis.
 *
 * ## No lanza
 *
 * Devuelve el asset o el problema, como `abrirDocumento`. Quien llama está en un
 * `onChange` de un `<input type="file">` y una excepción ahí no la recoge nadie.
 */
import { api } from '@/shared/api/client';
import { aProblema, type Problema } from '@/shared/api/errores';

/** Los cinco usos de `media_assets`. No hay forma de subir nada sin uno. */
export type UsoDeFichero =
  'profile' | 'logo' | 'cover' | 'catalog' | 'document';

export interface Asset {
  readonly id: string;
  readonly purpose: UsoDeFichero;
  readonly mime_type: string;
  readonly bytes: number;
  readonly width: number | null;
  readonly height: number | null;
  readonly uploaded_at: string | null;
}

/**
 * Lo que admite cada uso, **copiado del contrato**.
 *
 * Está aquí para poder decirlo antes de subir ocho megas por una red del polideportivo
 * y recibir un `422` al final. El servidor lo comprueba igualmente: esto no es la
 * regla, es la cortesía de no hacer esperar para nada.
 */
export const LIMITES: Readonly<
  Record<UsoDeFichero, { tipos: readonly string[]; megas: number }>
> = {
  profile: { tipos: ['image/jpeg', 'image/png', 'image/webp'], megas: 5 },
  logo: { tipos: ['image/png', 'image/svg+xml'], megas: 2 },
  cover: { tipos: ['image/jpeg', 'image/webp'], megas: 8 },
  catalog: { tipos: ['image/jpeg', 'image/webp'], megas: 4 },
  document: { tipos: ['application/pdf'], megas: 10 },
};

/** Para el `accept` del `<input type="file">`. */
export const aceptaDe = (uso: UsoDeFichero): string =>
  LIMITES[uso].tipos.join(',');

/**
 * El ancho y el alto de una imagen, leídos del fichero.
 *
 * Tres respuestas y **las tres significan cosas distintas**, que es lo que la primera
 * versión mezclaba:
 *
 * - `'no-aplica'` — no es una imagen de mapa de bits. Un PDF no tiene medidas, y un
 *   SVG es vectorial: no hay hueco que reservar, y `createImageBitmap` no lo admite en
 *   todos los navegadores.
 * - `{ width, height }` — se han podido leer.
 * - `'ilegible'` — **es una imagen y el navegador no la sabe decodificar**. Está
 *   dañada, o no es lo que su extensión dice.
 *
 * El último caso devolvía `null` como el primero, así que se mandaba una imagen sin
 * medidas y el servidor contestaba «width: Obligatorio para imágenes» — un error que
 * habla de un campo que la persona no ha visto nunca. Ahora se dice lo que pasa: que
 * esa foto no se puede leer. Visto el 22-09-2026 subiendo un PNG roto.
 */
type Medidas = { width: number; height: number } | 'no-aplica' | 'ilegible';

async function medidas(fichero: File): Promise<Medidas> {
  if (!fichero.type.startsWith('image/')) return 'no-aplica';
  if (fichero.type === 'image/svg+xml') return 'no-aplica';

  try {
    const bitmap = await createImageBitmap(fichero);
    const w = bitmap.width;
    const h = bitmap.height;
    bitmap.close();
    return { width: w, height: h };
  } catch {
    return 'ilegible';
  }
}

export interface Subida {
  readonly uso: UsoDeFichero;
  readonly fichero: File;
  /** Para los usos que cuelgan de un club: `logo`, `cover`, `catalog`, `document`. */
  readonly club?: string | undefined;
  /** `certificado_delitos`, `seguro`, `licencia`… Solo con `purpose: document`. */
  readonly clase?: string | undefined;
}

/**
 * Sube y confirma. Devuelve el asset listo para usar, o el problema.
 */
export async function subirFichero({
  uso,
  fichero,
  club,
  clase,
}: Subida): Promise<{ asset: Asset } | { problema: Problema }> {
  const limite = LIMITES[uso];

  if (!limite.tipos.includes(fichero.type)) {
    return {
      problema: {
        status: 422,
        title: 'Ese tipo de fichero no vale aquí',
        detail: `Aquí se admite ${limite.tipos.join(', ')}.`,
        code: 'tipo_no_admitido',
      },
    };
  }

  if (fichero.size > limite.megas * 1024 * 1024) {
    return {
      problema: {
        status: 422,
        title: 'El fichero es demasiado grande',
        detail: `El máximo son ${limite.megas} MB y este ocupa ${(
          fichero.size /
          1024 /
          1024
        ).toFixed(1)} MB.`,
        code: 'demasiado_grande',
      },
    };
  }

  const tamano = await medidas(fichero);

  if (tamano === 'ilegible') {
    return {
      problema: {
        status: 422,
        title: 'No hemos podido leer esa imagen',
        detail:
          'El fichero está dañado o no es del tipo que dice su nombre. Prueba a abrirlo primero y a volver a guardarlo.',
        code: 'imagen_ilegible',
      },
    };
  }

  // ── 1 · El billete ──────────────────────────────────────────────────────
  const pedido = await api.v1.uploads.post({
    purpose: uso,
    mime_type: fichero.type,
    bytes: fichero.size,
    ...(tamano === 'no-aplica' ? {} : tamano),
    ...(club === undefined ? {} : { academy_id: club }),
    ...(clase === undefined ? {} : { document_kind: clase }),
  });

  if (pedido.error) return { problema: aProblema(pedido.error) };

  const billete = pedido.data as unknown as {
    asset_id: string;
    upload_url: string;
  };

  // ── 2 · Los bytes, directos al almacén ──────────────────────────────────
  try {
    const r = await fetch(billete.upload_url, {
      method: 'PUT',
      // Sin esto la confirmación rechaza la subida: ver la cabecera.
      headers: { 'content-type': fichero.type },
      body: fichero,
    });

    if (!r.ok) {
      return {
        problema: {
          status: r.status,
          title: 'No hemos podido subir el fichero',
          detail:
            'El almacén ha rechazado la subida. Vuelve a intentarlo; si sigue fallando, prueba con otro fichero.',
          code: 'subida_rechazada',
        },
      };
    }
  } catch {
    return {
      problema: {
        status: 0,
        title: 'No hemos podido subir el fichero',
        detail: 'Se ha cortado la conexión a mitad. Vuelve a intentarlo.',
        code: 'sin_conexion',
      },
    };
  }

  // ── 3 · La confirmación, que es la que cierra el agujero ────────────────
  const confirmado = await api.v1
    .uploads({ assetId: billete.asset_id })
    .complete.post();

  if (confirmado.error) return { problema: aProblema(confirmado.error) };

  return { asset: confirmado.data as unknown as Asset };
}
